import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { estatesService } from "@/services/estates";

const EstateContext = createContext(null);

export function EstateProvider({ children }) {
  const { user, isLoadingAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [myEstates, setMyEstates] = useState([]);
  const [activeEstate, setActiveEstateState] = useState(null);
  const [isLoadingEstates, setIsLoadingEstates] = useState(true);
  // Tracks which user ID the current myEstates was actually fetched for.
  // Prevents Layout from redirecting when myEstates=[] is stale (loaded for a different/null user).
  const [loadedForUserId, setLoadedForUserId] = useState(null);

  const storageKey = user?.id ? `active_estate_${user.id}` : null;

  // Load all estates for the current user
  const loadEstates = useCallback(async () => {
    // Always mark as loading immediately — prevents Layout guard from seeing
    // stale isLoadingEstates=false + myEstates=[] during auth retry races
    setIsLoadingEstates(true);

    if (!user?.id || user?.user_role !== "estate_manager") {
      setMyEstates([]);
      setActiveEstateState(null);
      setLoadedForUserId(user?.id ?? null);
      setIsLoadingEstates(false);
      return;
    }

    setIsLoadingEstates(true);
    try {
      const estates = await estatesService.getMyEstates(user.id);
      setMyEstates(estates);
      setLoadedForUserId(user.id);

      // Resolve active estate: URL param → localStorage → first estate → null
      const urlEstateId = searchParams.get("estate");
      const storedEstateId = storageKey ? localStorage.getItem(storageKey) : null;

      const validIds = new Set(estates.map(e => e.id));

      let resolvedId = null;
      if (urlEstateId && validIds.has(urlEstateId)) {
        resolvedId = urlEstateId;
      } else if (storedEstateId && validIds.has(storedEstateId)) {
        resolvedId = storedEstateId;
      } else if (estates.length > 0) {
        resolvedId = estates[0].id;
      }

      // Clear stale localStorage entry
      if (storedEstateId && !validIds.has(storedEstateId) && storageKey) {
        localStorage.removeItem(storageKey);
      }

      const resolved = estates.find(e => e.id === resolvedId) || null;
      setActiveEstateState(resolved);

      // Persist to localStorage
      if (resolved && storageKey) {
        localStorage.setItem(storageKey, resolved.id);
      }
    } catch (err) {
      console.error("EstateContext: failed to load estates", err);
      setMyEstates([]);
      setActiveEstateState(null);
    } finally {
      setIsLoadingEstates(false);
    }
  }, [user?.id, user?.user_role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load on mount and whenever the user changes
  useEffect(() => {
    if (!isLoadingAuth) {
      loadEstates();
    }
  }, [isLoadingAuth, loadEstates]);

  /**
   * Switch the active estate.
   * - Validates the id is in myEstates
   * - Clears React Query cache for the old estate
   * - Persists to localStorage
   * - Updates URL query param (navigate, no push so back-button doesn't loop)
   */
  const setActiveEstate = useCallback((estateId) => {
    const target = myEstates.find(e => e.id === estateId);
    if (!target) {
      console.warn("EstateContext: attempted to switch to unknown estate", estateId);
      return;
    }

    const previousId = activeEstate?.id;

    // Hard-reset React Query cache for the outgoing estate
    if (previousId && previousId !== estateId) {
      queryClient.removeQueries({ predicate: q => q.queryKey.includes(previousId) });
    }

    setActiveEstateState(target);

    if (storageKey) {
      localStorage.setItem(storageKey, estateId);
    }

    // Sync URL — replace so back button doesn't cycle through each switch
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("estate", estateId);
      return next;
    }, { replace: true });
  }, [myEstates, activeEstate?.id, storageKey, queryClient, setSearchParams]);

  /**
   * Reload the estates list (e.g. after creating a new estate or accepting an invite).
   */
  const refreshEstates = useCallback(() => loadEstates(), [loadEstates]);

  return (
    <EstateContext.Provider
      value={{
        myEstates,
        activeEstate,
        activeEstateId: activeEstate?.id ?? null,
        isLoadingEstates,
        loadedForUserId,
        setActiveEstate,
        refreshEstates,
      }}
    >
      {children}
    </EstateContext.Provider>
  );
}

/**
 * Hook to access the active estate context.
 * Must be used inside <EstateProvider>.
 */
export function useEstateContext() {
  const ctx = useContext(EstateContext);
  if (!ctx) throw new Error("useEstateContext must be used inside EstateProvider");
  return ctx;
}
